import React, { useEffect } from 'react';
import ReactDOM from 'react-dom';

/**
 * Custom Alert Modal Component
 * Replaces react-bootstrap-sweetalert with a pure React + SCSS solution
 *
 * API matches react-bootstrap-sweetalert for compatibility
 *
 * @param {boolean} show - Controls modal visibility
 * @param {string} type - Alert type: 'info', 'success', 'warning', 'error' (default: 'info')
 * @param {string} title - Modal title (optional)
 * @param {ReactNode} children - Alert content
 * @param {string} confirmBtnText - Confirm button text (default: 'OK')
 * @param {string} cancelBtnText - Cancel button text (default: 'Cancel')
 * @param {boolean} showConfirm - Show confirm button (default: true)
 * @param {boolean} showCancel - Show cancel button (default: false)
 * @param {boolean} showCloseButton - Show close button (X) in top-right corner (default: false)
 * @param {function} onConfirm - Callback when confirm button clicked
 * @param {function} onCancel - Callback when cancel button clicked
 * @param {function} onClose - Callback when close button (X) clicked or ESC pressed
 * @param {boolean} closeOnClickOutside - Allow closing by clicking backdrop (default: true)
 * @param {string} confirmBtnCssClass - Additional CSS classes for confirm button
 * @param {string} cancelBtnCssClass - Additional CSS classes for cancel button
 * @param {boolean} disabled - Disable both buttons (default: false)
 * @param {string} containerClassName - Additional CSS classes for the modal container
 * @param showLoadingInConform
 * @param disabledText
 */
const AlertModal = ({
    show = false,
    type = '',
    title = '',
    children,
    confirmBtnText = 'OK',
    cancelBtnText = 'Cancel',
    showConfirm = true,
    showCancel = false,
    showCloseButton = false,
    onConfirm,
    onCancel,
    onClose,
    closeOnClickOutside = true,
    confirmBtnCssClass = '',
    cancelBtnCssClass = '',
    disabled = false,
    containerClassName = '',
    showLoadingInConform = false,
    disabledText = 'Loading..'
}) => {
    /**
     * Get Font Awesome icon class based on alert type
     */
    const getIconClass = () => {
        switch (type) {
            case 'success':
                return 'fa fa-check-circle';
            case 'warning':
                return 'fa fa-exclamation-triangle';
            case 'error':
                return 'fa fa-times-circle';
            case 'info':
            default:
                return 'fa fa-info-circle';
        }
    };

    /**
     * Handle confirm button click
     */
    const handleConfirm = () => {
        if (!disabled && onConfirm) {
            onConfirm();
        }
    };

    /**
     * Handle cancel button click
     */
    const handleCancel = () => {
        if (!disabled && onCancel) {
            onCancel();
        }
    };

    /**
     * Handle close button (X) click
     */
    const handleClose = () => {
        if (!disabled && onClose) {
            onClose();
        }
    };

    /**
     * Handle backdrop click
     */
    const handleBackdropClick = (e) => {
        if (closeOnClickOutside && !disabled && e.target === e.currentTarget) {
            if (onClose) {
                onClose();
            } else if (onConfirm) {
                onConfirm();
            }
        }
    };

    /**
     * Handle ESC key press
     */
    useEffect(() => {
        const handleEscKey = (e) => {
            if (e.key === 'Escape' && show && !disabled) {
                if (onClose) {
                    onClose();
                } else if (onConfirm) {
                    onConfirm();
                }
            }
        };

        if (show) {
            document.addEventListener('keydown', handleEscKey);
            // Prevent body scroll when modal is open
            document.body.style.overflow = 'hidden';
        }

        return () => {
            document.removeEventListener('keydown', handleEscKey);
            document.body.style.overflow = 'unset';
        };
    }, [show, onClose, onConfirm, disabled]);

    // Don't render if not visible
    if (!show) return null;

    const modalContent = (
        <div className="alert-modal">
            <div
                className="alert-modal__overlay"
                onClick={handleBackdropClick}
                aria-hidden="true"
            >
                <div
                    className={`alert-modal__container alert-modal__container--${type} ${containerClassName}`.trim()}
                    role="dialog"
                    aria-modal="true"
                    aria-labelledby={title ? "alert-modal-title" : undefined}
                    aria-describedby="alert-modal-content"
                >
                    {/* Close Button (X) */}
                    {showCloseButton && (
                        <button
                            className="alert-modal__close"
                            onClick={handleClose}
                            disabled={disabled}
                            aria-label="Close"
                            type="button"
                        >
                            ×
                        </button>
                    )}

                    {/* Icon — render only if type is provided */}
                    {type && (
                    <div className={`alert-modal__icon alert-modal__icon--${type}`}>
                        <i className={getIconClass()} aria-hidden="true"></i>
                    </div>)}

                    {/* Title */}
                    {title && (
                        <div className="alert-modal__header">
                            <h2
                                id="alert-modal-title"
                                className="alert-modal__title"
                            >
                                {title}
                            </h2>
                        </div>
                    )}

                    {/* Content */}
                    <div
                        id="alert-modal-content"
                        className="alert-modal__content"
                    >
                        {children}
                    </div>

                    {/* Action Buttons */}
                    <div className="alert-modal__actions">
                        {showConfirm && (
                            <button
                                className={`alert-modal__btn alert-modal__btn--confirm ${confirmBtnCssClass}`.trim()}
                                onClick={showLoadingInConform ? undefined : handleConfirm}
                                type="button"
                                style={showLoadingInConform ? { cursor: 'not-allowed' } : undefined}
                            >
                                {showLoadingInConform ? (
                                    <>{disabledText}</>
                                ) : (
                                    <>{confirmBtnText}</>
                                )}
                            </button>
                        )}
                        {showCancel && (
                            <button
                                className={`alert-modal__btn alert-modal__btn--cancel ${cancelBtnCssClass}`.trim()}
                                onClick={showLoadingInConform ? undefined : handleCancel}
                                type="button"
                                style={showLoadingInConform ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
                            >
                                {cancelBtnText}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    // Render in portal for proper z-index stacking
    return ReactDOM.createPortal(
        modalContent,
        document.body
    );
};

export default AlertModal;
